import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { format } from "date-fns";
import { openPendingFile } from "../../../utils/download";
import {
  ArrowRightLeft,
  CalendarClock,
  FileText,
  Mail,
  MailOpen,
  MessageSquare,
  Paperclip,
  Phone,
  Reply,
  Settings,
  Trash2,
  Users,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/Button";
import { ManagedFileLink } from "@/components/ui/ManagedFileLink";
import { SectionCard } from "@/features/power_projects/components/ui";
import { RichTextEditor } from "@/components/ui/RichTextEditor";
import { LeadRichText } from "@/features/leads/components/LeadRichText";
import {
  useAddActivity,
  useDeleteLeadFile,
  useLeadActivities,
  useUploadLeadFile,
  type ActivityKind,
  type Lead,
  type LeadActivity,
} from "@/features/leads/api/leads";
import { fileTooLargeMessage, findTooLargeFile, uploadErrorMessage } from "@/utils/managedFile";

/**
 * Oś czasu firmy — wszystko, co się z nią działo, w jednym miejscu.
 *
 * Wpisy automatyczne (wysłany mail, zmiana statusu, zadanie) są wyszarzone, żeby
 * ręczna notatka człowieka nie utonęła w szumie maszyny. Ręcznie da się dopisać
 * tylko to, czego system nie widzi sam: telefon, spotkanie, notatkę, mail spoza
 * systemu.
 *
 * Załącznik może wisieć bezpośrednio pod wpisem (skan notatki ze spotkania należy
 * do spotkania) — plik i tak ląduje też w panelu „Pliki" na karcie.
 */

const ICONS: Record<ActivityKind, typeof Phone> = {
  note: MessageSquare,
  call: Phone,
  meeting: Users,
  email_out: Mail,
  email_in: MailOpen,
  status_change: ArrowRightLeft,
  task: CalendarClock,
  file: FileText,
  system: Settings,
};

const MANUAL_KINDS: { value: ActivityKind; label: string }[] = [
  { value: "call", label: "Telefon" },
  { value: "note", label: "Notatka" },
  { value: "meeting", label: "Spotkanie" },
  { value: "email_out", label: "Mail wysłany" },
  { value: "email_in", label: "Mail otrzymany" },
];

export function LeadTimeline({ lead }: { lead: Lead }) {
  const { data: activities } = useLeadActivities(lead.id);
  const addActivity = useAddActivity();
  const uploadFile = useUploadLeadFile();

  const [kind, setKind] = useState<ActivityKind>("call");
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [contact, setContact] = useState("");
  const [occurredAt, setOccurredAt] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [error, setError] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  const submit = async () => {
    if (!title.trim() && !body.trim()) {
      setError("Wpis musi mieć tytuł albo treść.");
      return;
    }
    // Before the save — the entry would otherwise stand without the file it was for.
    const tooLarge = findTooLargeFile(files);
    if (tooLarge) {
      setError(fileTooLargeMessage(tooLarge));
      return;
    }
    setError("");
    let entry: LeadActivity;
    try {
      entry = await addActivity.mutateAsync({
        leadId: lead.id,
        kind,
        title: title.trim(),
        body: body.trim(),
        contact: contact || null,
        occurred_at: occurredAt ? new Date(occurredAt).toISOString() : null,
      });
    } catch {
      setError("Nie udało się zapisać wpisu.");
      return;
    }
    // Wpis już stoi — pliki dogrywamy pod niego jeden po drugim, a te, które
    // padły, zostają na liście, żeby dało się ponowić bez dublowania wpisu.
    const failed: File[] = [];
    const reasons: string[] = [];
    for (const file of files) {
      try {
        await uploadFile.mutateAsync({ leadId: lead.id, file, activityId: entry.id });
      } catch (err) {
        failed.push(file);
        reasons.push(uploadErrorMessage(err, ""));
      }
    }
    setTitle("");
    setBody("");
    setOccurredAt("");
    setFiles(failed);
    if (failed.length) {
      setError(
        `Wpis zapisany, ale nie udało się wgrać: ${failed.map((f) => f.name).join(", ")}. ` +
          reasons.filter(Boolean).join(" "),
      );
    }
  };

  return (
    <SectionCard title="Historia kontaktu" bodyClassName="p-0">
      <div className="space-y-2 border-b border-[var(--color-border)] p-4">
        <div className="flex flex-wrap gap-2">
          {MANUAL_KINDS.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => setKind(option.value)}
              className={
                kind === option.value
                  ? "border border-[var(--color-primary)] px-2 py-1 text-xs text-[var(--color-primary)]"
                  : "border border-[var(--color-border)] px-2 py-1 text-xs text-[var(--color-text-secondary)]"
              }
            >
              {option.label}
            </button>
          ))}
        </div>
        <input
          className="input-base"
          placeholder="Krótko: co się wydarzyło"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />
        <RichTextEditor
          value={body}
          onChange={setBody}
          placeholder="Szczegóły — ustalenia, kto co obiecał, na kiedy"
        />
        {files.length > 0 && (
          <ul className="flex flex-wrap gap-1.5">
            {files.map((file, index) => (
              <li
                key={`${file.name}-${index}`}
                className="flex items-center gap-1 border border-[var(--color-border)] px-2 py-0.5 text-xs text-[var(--color-text-secondary)]"
              >
                <Paperclip size={11} />
                <button
                  type="button"
                  onClick={() => openPendingFile(file)}
                  className="max-w-[14rem] truncate hover:underline hover:text-[var(--color-primary)]"
                  title={`${file.name} — kliknij, aby podejrzeć`}
                >
                  {file.name}
                </button>
                <button
                  type="button"
                  onClick={() => setFiles(files.filter((_, i) => i !== index))}
                  className="text-[var(--color-text-muted)] hover:text-[var(--color-primary)]"
                  aria-label={`Usuń plik ${file.name}`}
                >
                  <X size={11} />
                </button>
              </li>
            ))}
          </ul>
        )}
        <div className="flex flex-wrap items-center gap-2">
          <select className="input-base sm:w-56" value={contact} onChange={(e) => setContact(e.target.value)}>
            <option value="">Bez wskazania osoby</option>
            {lead.contacts.map((person) => (
              <option key={person.id} value={person.id}>
                {person.display_name || person.email}
              </option>
            ))}
          </select>
          <input
            type="datetime-local"
            className="input-base sm:w-56"
            value={occurredAt}
            onChange={(e) => setOccurredAt(e.target.value)}
            title="Kiedy to się wydarzyło (puste = teraz)"
          />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="inline-flex items-center gap-1 border border-[var(--color-border)] px-2 py-1.5 text-xs text-[var(--color-text-secondary)] hover:text-[var(--color-primary)]"
            title="Dołącz pliki do wpisu"
          >
            <Paperclip size={12} /> Załącz
          </button>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            className="hidden"
            onChange={(e) => {
              setFiles([...files, ...Array.from(e.target.files ?? [])]);
              e.target.value = "";
            }}
          />
          <Button
            size="sm"
            onClick={submit}
            loading={addActivity.isPending || uploadFile.isPending}
          >
            Dopisz do historii
          </Button>
        </div>
        {error && <p className="text-xs text-[var(--color-primary)]">{error}</p>}
      </div>

      {(activities ?? []).length === 0 ? (
        <p className="p-4 text-sm text-[var(--color-text-muted)]">
          Jeszcze nic się nie wydarzyło.
        </p>
      ) : (
        <ol className="p-4 pb-0">
          {(activities ?? []).map((entry) => {
            const Icon = ICONS[entry.kind] ?? MessageSquare;
            return (
              <li key={entry.id} className="flex gap-3 pb-3 last:pb-0">
                <div
                  className={
                    entry.is_automatic
                      ? "mt-0.5 text-[var(--color-text-muted)]"
                      : "mt-0.5 text-[var(--color-primary)]"
                  }
                >
                  <Icon size={14} />
                </div>
                <div className="min-w-0 flex-1 border-b border-[var(--color-border)] pb-3 last:border-0">
                  <div className="flex flex-wrap items-baseline gap-x-2 text-sm">
                    <span
                      className={
                        entry.is_automatic
                          ? "text-[var(--color-text-secondary)]"
                          : "font-medium text-[var(--color-text-primary)]"
                      }
                    >
                      {entry.title || entry.kind_label}
                    </span>
                    <span className="text-xs text-[var(--color-text-muted)]">
                      {format(new Date(entry.occurred_at), "dd.MM.yyyy HH:mm")}
                      {entry.created_by_name ? ` · ${entry.created_by_name}` : ""}
                      {entry.contact_name ? ` · ${entry.contact_name}` : ""}
                    </span>
                    {!entry.is_automatic && (
                      <EntryAttachButton leadId={lead.id} activityId={entry.id} />
                    )}
                  </div>
                  {entry.body && (
                    <LeadRichText
                      content={entry.body}
                      className="mt-1 text-sm text-[var(--color-text-secondary)]"
                    />
                  )}
                  {entry.attachments?.length > 0 && (
                    <ul className="mt-1.5 space-y-1">
                      {entry.attachments.map((file) => (
                        <EntryAttachmentRow key={file.id} file={file} />
                      ))}
                    </ul>
                  )}
                  {/* Mail od firmy da się otworzyć w skrzynce i odpisać na
                      miejscu — bez tego trzeba zgadywać, gdzie leży ten wątek. */}
                  {entry.kind === "email_in" &&
                    typeof entry.metadata?.inbound_id === "string" && (
                      <Link
                        to={`/leads/inbox?id=${entry.metadata.inbound_id}`}
                        className="mt-1 inline-flex items-center gap-1 text-[11px] text-[var(--color-text-muted)] hover:text-[var(--color-primary)]"
                      >
                        <Reply size={11} /> otwórz w skrzynce i odpisz
                      </Link>
                    )}
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </SectionCard>
  );
}

/** Spinacz na istniejącym wpisie — dogrywa plik prosto pod niego. */
function EntryAttachButton({ leadId, activityId }: { leadId: string; activityId: string }) {
  const upload = useUploadLeadFile();
  const inputRef = useRef<HTMLInputElement>(null);
  const [failure, setFailure] = useState("");
  const failed = failure !== "";

  return (
    <>
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={upload.isPending}
        className={
          failed
            ? "text-[var(--color-primary)]"
            : "text-[var(--color-text-muted)] hover:text-[var(--color-primary)]"
        }
        title={failed ? failure : "Dodaj załącznik do wpisu"}
      >
        <Paperclip size={12} className={upload.isPending ? "animate-pulse" : undefined} />
      </button>
      <input
        ref={inputRef}
        type="file"
        multiple
        className="hidden"
        onChange={async (e) => {
          const picked = Array.from(e.target.files ?? []);
          e.target.value = "";
          setFailure("");
          const reasons: string[] = [];
          for (const file of picked) {
            try {
              await upload.mutateAsync({ leadId, file, activityId });
            } catch (err) {
              reasons.push(uploadErrorMessage(err, `Nie udało się wgrać pliku ${file.name} — spróbuj ponownie.`));
            }
          }
          setFailure(reasons.join(" "));
        }}
      />
      {/* The red paperclip alone said nothing — the reason has to be readable. */}
      {failed && <span className="text-[11px] text-[var(--color-primary)]">{failure}</span>}
    </>
  );
}

function EntryAttachmentRow({
  file,
}: {
  file: LeadActivity["attachments"][number];
}) {
  const remove = useDeleteLeadFile();
  return (
    <li className="flex items-center gap-1.5 text-xs">
      <Paperclip size={11} className="shrink-0 text-[var(--color-text-muted)]" />
      <ManagedFileLink
        file={{
          url: file.url,
          scan_status: file.scan_status,
          original_filename: file.original_filename,
        }}
      >
        {file.label || file.original_filename}
      </ManagedFileLink>
      <button
        type="button"
        onClick={() => remove.mutate(file.id)}
        disabled={remove.isPending}
        className="shrink-0 text-[var(--color-text-muted)] hover:text-[var(--color-primary)]"
        title="Usuń załącznik"
      >
        <Trash2 size={11} />
      </button>
    </li>
  );
}
